// src/components/DocWriterImage.jsx
// UPDATED 2026-08-13 v3: preserves the v2 Chart-style UI and fixes the image draft/live handoff:
// every preview control now commits from the latest draft state, Canvas receives the complete image
// value/meta contract, stale incoming props cannot overwrite a just-saved edit, caption/supplemental
// alignment is handed to Canvas, and strict outer-area pointer ownership remains unchanged.

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#475569";
const PANEL_BG = "#ffffff";
const SOFT_BG = "#f8fafc";
const ACCENT = "#1E2A78";
const BORDER = "rgba(148,163,184,0.35)";

export const DOC_WRITER_IMAGE_COMPONENT_VERSION = "2026.08.13-v3";
export const DOC_WRITER_IMAGE_ATTR = "data-doc-writer-image";
export const DOC_WRITER_IMAGE_ID_ATTR = "data-doc-writer-image-id";
export const DOC_WRITER_IMAGE_PRINT_ATTR = "data-doc-writer-image-print-surface";
export const DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR = "data-doc-editor-only";
export const DOC_WRITER_IMAGE_DRAG_HANDLE_ATTR = "data-doc-visual-drag-handle";
export const DOC_WRITER_IMAGE_RESIZE_HANDLE_ATTR = "data-doc-visual-resize-handle";

const DEFAULT_WIDTH = 520;
const DEFAULT_HEIGHT = 320;
const MIN_WIDTH = 120;
const MIN_HEIGHT = 80;
const MAX_WIDTH = 1600;
const MAX_HEIGHT = 1400;
const ACCEPTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const SIZE_PRESETS = {
  small: { label: "Small", width: 300 },
  medium: { label: "Medium", width: 480 },
  large: { label: "Large", width: 620 },
  full: { label: "Full width", width: null },
};

const SHADOWS = {
  none: "none",
  soft: "0 8px 24px rgba(15,23,42,0.12)",
  strong: "0 16px 36px rgba(15,23,42,0.20)",
};

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function safeNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function normalizeAlignment(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(normalized) ? normalized : "center";
}

function normalizeFit(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["contain", "cover", "fill", "scale-down", "none"].includes(normalized)
    ? normalized
    : "contain";
}

function normalizeBorderStyle(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["solid", "dashed", "dotted", "double", "none"].includes(normalized)
    ? normalized
    : "solid";
}

function normalizeShadow(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(SHADOWS, normalized) ? normalized : "none";
}

function boolValue(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value == null || String(value).trim() === "") return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
}

function normalizeColor(value, fallback) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^rgba?\(/i.test(raw)) return raw;
  if (/^hsla?\(/i.test(raw)) return raw;
  if (/^[a-z]+$/i.test(raw)) return raw;
  if (raw === "transparent") return raw;
  return fallback;
}

function safeColorPickerValue(value, fallback = "#ffffff") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw.slice(1).split("").map((character) => character + character).join("")}`;
  }
  return fallback;
}


function createImageSupplementalId(prefix = "line") {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (_) {}
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function stableImageSupplementalId(kind, index, text) {
  const source = `${kind}:${index}:${String(text || "")}`;
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${kind}line-${(hash >>> 0).toString(36)}`;
}

function normalizeImageSupplementalLine(line, kind = "title", index = 0) {
  const source = line && typeof line === "object" && !Array.isArray(line)
    ? line
    : { text: String(line ?? "") };
  const normalizedKind = kind === "note" ? "note" : "title";
  const lineText = String(source.text ?? source.value ?? "");
  return {
    id: String(source.id || stableImageSupplementalId(normalizedKind, index, lineText)),
    text: lineText,
    alignment: normalizeAlignment(source.alignment),
    fontSizePx: clamp(safeNumber(source.fontSizePx, normalizedKind === "title" ? 14 : 12), 3, 96),
    bold: typeof source.bold === "boolean" ? source.bold : normalizedKind === "title",
    italic: source.italic === true,
    underline: source.underline === true,
  };
}

function normalizeImageSupplementalLines(value, kind = "title") {
  if (!Array.isArray(value)) return [];
  return value.map((line, index) => normalizeImageSupplementalLine(line, kind, index));
}

function defaultMeta() {
  return {
    alignment: "center",
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    lockAspectRatio: true,
    zoom: 100,
    naturalWidth: 0,
    naturalHeight: 0,
    fit: "contain",
    positionX: 50,
    positionY: 50,
    rotation: 0,
    flipX: false,
    flipY: false,
    opacity: 100,
    brightness: 100,
    contrast: 100,
    saturation: 100,
    grayscale: 0,
    sepia: 0,
    blur: 0,
    background: "transparent",
    borderColor: "#cbd5e1",
    borderWidth: 0,
    borderStyle: "solid",
    borderRadius: 0,
    padding: 0,
    shadow: "none",
    topTitles: [],
    bottomNotes: [],
    showCaption: false,
    caption: "",
    captionAlignment: "center",
    captionFontSize: 12,
    captionColor: TEXT_MUTED,
    captionBackground: "transparent",
    altText: "",
    fileName: "",
    mimeType: "",
  };
}

function normalizeMeta(meta = {}, widthProp, heightProp, alignmentProp) {
  const base = defaultMeta();
  const widthCandidate = widthProp ?? meta.width ?? base.width;
  const heightCandidate = heightProp ?? meta.height ?? base.height;
  return {
    ...base,
    ...meta,
    alignment: normalizeAlignment(alignmentProp ?? meta.alignment ?? base.alignment),
    width: clamp(safeNumber(widthCandidate, base.width), MIN_WIDTH, MAX_WIDTH),
    height: clamp(safeNumber(heightCandidate, base.height), MIN_HEIGHT, MAX_HEIGHT),
    lockAspectRatio: boolValue(meta.lockAspectRatio, true),
    zoom: clamp(safeNumber(meta.zoom, 100), 25, 400),
    naturalWidth: Math.max(0, safeNumber(meta.naturalWidth, 0)),
    naturalHeight: Math.max(0, safeNumber(meta.naturalHeight, 0)),
    fit: normalizeFit(meta.fit),
    positionX: clamp(safeNumber(meta.positionX, 50), 0, 100),
    positionY: clamp(safeNumber(meta.positionY, 50), 0, 100),
    rotation: clamp(safeNumber(meta.rotation, 0), -180, 180),
    flipX: boolValue(meta.flipX, false),
    flipY: boolValue(meta.flipY, false),
    opacity: clamp(safeNumber(meta.opacity, 100), 0, 100),
    brightness: clamp(safeNumber(meta.brightness, 100), 0, 300),
    contrast: clamp(safeNumber(meta.contrast, 100), 0, 300),
    saturation: clamp(safeNumber(meta.saturation, 100), 0, 300),
    grayscale: clamp(safeNumber(meta.grayscale, 0), 0, 100),
    sepia: clamp(safeNumber(meta.sepia, 0), 0, 100),
    blur: clamp(safeNumber(meta.blur, 0), 0, 20),
    background: normalizeColor(meta.background, "transparent"),
    borderColor: normalizeColor(meta.borderColor, "#cbd5e1"),
    borderWidth: clamp(safeNumber(meta.borderWidth, 0), 0, 20),
    borderStyle: normalizeBorderStyle(meta.borderStyle),
    borderRadius: clamp(safeNumber(meta.borderRadius, 0), 0, 100),
    padding: clamp(safeNumber(meta.padding, 0), 0, 80),
    shadow: normalizeShadow(meta.shadow),
    topTitles: normalizeImageSupplementalLines(meta.topTitles ?? meta.top_titles, "title"),
    bottomNotes: normalizeImageSupplementalLines(meta.bottomNotes ?? meta.bottom_notes, "note"),
    showCaption: boolValue(meta.showCaption, false),
    caption: String(meta.caption || ""),
    captionAlignment: normalizeAlignment(meta.captionAlignment),
    captionFontSize: clamp(safeNumber(meta.captionFontSize, 12), 7, 40),
    captionColor: normalizeColor(meta.captionColor, TEXT_MUTED),
    captionBackground: normalizeColor(meta.captionBackground, "transparent"),
    altText: String(meta.altText || ""),
    fileName: String(meta.fileName || ""),
    mimeType: String(meta.mimeType || ""),
  };
}

export function normalizeDocWriterImageValue(value, overrides = {}) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const src = String(value.src ?? value.url ?? value.dataUrl ?? value.source ?? "");
    const meta = normalizeMeta(
      value.meta && typeof value.meta === "object" ? value.meta : value,
      overrides.width,
      overrides.height,
      overrides.alignment
    );
    return { type: "docwriter_image", src, meta };
  }

  const raw = String(value ?? "").trim();
  if (raw.startsWith("{")) {
    const parsed = safeJsonParse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return normalizeDocWriterImageValue(parsed, overrides);
    }
  }

  return {
    type: "docwriter_image",
    src: raw,
    meta: normalizeMeta({}, overrides.width, overrides.height, overrides.alignment),
  };
}

export function serializeDocWriterImageValue(src, meta) {
  return {
    type: "docwriter_image",
    src: String(src || ""),
    meta: normalizeMeta(meta),
  };
}

function imageStateSignature(src, meta) {
  try {
    return JSON.stringify(serializeDocWriterImageValue(src, meta));
  } catch (_) {
    return `${String(src || "")}::${String(meta?.width || "")}x${String(meta?.height || "")}`;
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Unable to read image."));
    reader.readAsDataURL(file);
  });
}

function loadImageDimensions(src) {
  return new Promise((resolve) => {
    if (!src || typeof Image === "undefined") {
      resolve({ width: 0, height: 0 });
      return;
    }
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 });
    image.onerror = () => resolve({ width: 0, height: 0 });
    image.src = src;
  });
}

function imageTransform(meta) {
  const rotate = safeNumber(meta.rotation, 0);
  const zoom = clamp(safeNumber(meta.zoom, 100), 25, 400) / 100;
  const scaleX = (boolValue(meta.flipX, false) ? -1 : 1) * zoom;
  const scaleY = (boolValue(meta.flipY, false) ? -1 : 1) * zoom;
  return `rotate(${rotate}deg) scale(${scaleX}, ${scaleY})`;
}

function imageFilter(meta) {
  return [
    `brightness(${clamp(safeNumber(meta.brightness, 100), 0, 300)}%)`,
    `contrast(${clamp(safeNumber(meta.contrast, 100), 0, 300)}%)`,
    `saturate(${clamp(safeNumber(meta.saturation, 100), 0, 300)}%)`,
    `grayscale(${clamp(safeNumber(meta.grayscale, 0), 0, 100)}%)`,
    `sepia(${clamp(safeNumber(meta.sepia, 0), 0, 100)}%)`,
    `blur(${clamp(safeNumber(meta.blur, 0), 0, 20)}px)`,
  ].join(" ");
}

function ImageSurface({
  src,
  meta,
  interactive = false,
  onPositionChange,
  onPositionDragStart,
  maxWidth = MAX_WIDTH,
  supplementalEditable = false,
  onSupplementalFocus = null,
  onSupplementalCommit = null,
  onSupplementalInput = null,
  canvasTextAlignmentHandoff = false,
}) {
  const dragRef = useRef(null);
  const effectiveWidth = clamp(safeNumber(meta.width, DEFAULT_WIDTH), MIN_WIDTH, Math.max(MIN_WIDTH, maxWidth || MAX_WIDTH));
  const effectiveHeight = clamp(safeNumber(meta.height, DEFAULT_HEIGHT), MIN_HEIGHT, MAX_HEIGHT);
  const positionX = clamp(safeNumber(meta.positionX, 50), 0, 100);
  const positionY = clamp(safeNumber(meta.positionY, 50), 0, 100);
  const topTitles = normalizeImageSupplementalLines(meta.topTitles, "title");
  const bottomNotes = normalizeImageSupplementalLines(meta.bottomNotes, "note");

  const handoffCanvasTextAlignment = (node, requestedAlignment) => {
    if (!node || !canvasTextAlignmentHandoff) return;
    const nextAlignment = normalizeAlignment(requestedAlignment);
    const marker = "data-docwriter-image-canvas-alignment-handoff";
    if (node.getAttribute(marker) === nextAlignment) return;
    node.style.setProperty("text-align", nextAlignment, "important");
    node.setAttribute(marker, nextAlignment);
  };

  const supplementalLine = (line, supplementalKind) => {
    const isTitle = supplementalKind === "title";
    return (
      <div
        key={`${supplementalKind}-${line.id}`}
        ref={(node) => handoffCanvasTextAlignment(node, line.alignment)}
        data-docwriter-image-supplemental-kind={supplementalKind}
        data-docwriter-image-supplemental-id={line.id}
        contentEditable={supplementalEditable || undefined}
        suppressContentEditableWarning
        spellCheck={supplementalEditable}
        onFocus={() => {
          if (supplementalEditable) {
            try { onSupplementalFocus?.(supplementalKind, line.id); } catch (_) {}
          }
        }}
        onPointerDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onClick={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
          if (event.key === "Enter") event.preventDefault();
        }}
        onInput={(event) => {
          if (!supplementalEditable) return;
          event.stopPropagation();
          try { onSupplementalInput?.(supplementalKind, line.id, String(event.currentTarget.textContent || "")); } catch (_) {}
        }}
        onBlur={(event) => {
          if (!supplementalEditable) return;
          try { onSupplementalCommit?.(supplementalKind, line.id, String(event.currentTarget.textContent || "")); } catch (_) {}
        }}
        style={{
          minHeight: 18,
          width: "100%",
          boxSizing: "border-box",
          textAlign: line.alignment,
          fontSize: `${line.fontSizePx}px`,
          fontWeight: line.bold ? 700 : 400,
          fontStyle: line.italic ? "italic" : "normal",
          textDecoration: line.underline ? "underline" : "none",
          lineHeight: 1.35,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          color: TEXT_DARK,
          margin: isTitle ? "0 0 4px" : "4px 0 0",
          padding: "1px 4px",
          outline: "none",
          cursor: supplementalEditable ? "text" : "default",
          userSelect: supplementalEditable ? "text" : "auto",
        }}
      >
        {line.text}
      </div>
    );
  };

  const beginPositionDrag = (event) => {
    if (!interactive || typeof onPositionChange !== "function" || !src) return;
    event.preventDefault();
    event.stopPropagation();
    try { onPositionDragStart?.(); } catch (_) {}
    const frame = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPositionX: positionX,
      startPositionY: positionY,
      width: Math.max(1, frame.width),
      height: Math.max(1, frame.height),
    };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const state = dragRef.current;
      if (!state) return;
      const dxPercent = ((moveEvent.clientX - state.startX) / state.width) * 100;
      const dyPercent = ((moveEvent.clientY - state.startY) / state.height) * 100;
      onPositionChange({
        x: clamp(state.startPositionX + dxPercent, 0, 100),
        y: clamp(state.startPositionY + dyPercent, 0, 100),
      });
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", up, true);
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);
  };

  const borderWidth = clamp(safeNumber(meta.borderWidth, 0), 0, 20);
  const borderStyle = borderWidth > 0 ? normalizeBorderStyle(meta.borderStyle) : "none";
  const padding = clamp(safeNumber(meta.padding, 0), 0, 80);
  const radius = clamp(safeNumber(meta.borderRadius, 0), 0, 100);

  return (
    <div
      style={{
        width: effectiveWidth,
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "grid",
        gap: topTitles.length || bottomNotes.length || boolValue(meta.showCaption, false) ? 8 : 0,
      }}
    >
      {topTitles.map((line) => supplementalLine(line, "title"))}

      <div
        onPointerDown={beginPositionDrag}
        title={interactive && src ? "Drag the image to reposition it inside the frame" : undefined}
        style={{
          position: "relative",
          width: "100%",
          height: effectiveHeight,
          boxSizing: "border-box",
          overflow: "hidden",
          padding,
          border: borderStyle === "none" ? "none" : `${borderWidth}px ${borderStyle} ${normalizeColor(meta.borderColor, "#cbd5e1")}`,
          borderRadius: radius,
          background: normalizeColor(meta.background, "transparent"),
          boxShadow: SHADOWS[normalizeShadow(meta.shadow)],
          cursor: interactive && src ? "move" : "default",
          touchAction: interactive ? "none" : "auto",
          userSelect: interactive ? "none" : "auto",
        }}
      >
        {src ? (
          <img
            src={src}
            alt={String(meta.altText || "")}
            draggable={false}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: normalizeFit(meta.fit),
              objectPosition: `${positionX}% ${positionY}%`,
              transform: imageTransform(meta),
              transformOrigin: "center center",
              opacity: clamp(safeNumber(meta.opacity, 100), 0, 100) / 100,
              filter: imageFilter(meta),
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: TEXT_MUTED, fontSize: 13, background: "#f8fafc" }}>
            Choose an image to begin.
          </div>
        )}
      </div>

      {boolValue(meta.showCaption, false) ? (
        <div
          ref={(node) => handoffCanvasTextAlignment(node, meta.captionAlignment)}
          data-docwriter-image-caption="true"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: String(meta.captionBackground || "transparent") === "transparent" ? 0 : "5px 8px",
            borderRadius: 6,
            textAlign: normalizeAlignment(meta.captionAlignment),
            color: normalizeColor(meta.captionColor, TEXT_MUTED),
            background: normalizeColor(meta.captionBackground, "transparent"),
            fontSize: clamp(safeNumber(meta.captionFontSize, 12), 7, 40),
            lineHeight: 1.4,
            overflowWrap: "anywhere",
          }}
        >
          {String(meta.caption || "").trim() || "Image caption"}
        </div>
      ) : null}

      {bottomNotes.map((line) => supplementalLine(line, "note"))}
    </div>
  );
}

function ColorField({ label, value, fallback, onChange, inputStyle, labelStyle }) {
  const textValue = String(value || fallback || "");
  return (
    <label style={labelStyle}>
      {label}
      <div style={{ display: "grid", gridTemplateColumns: "38px minmax(0, 1fr)", gap: 7 }}>
        <input
          type="color"
          value={safeColorPickerValue(textValue, safeColorPickerValue(fallback || "#ffffff"))}
          onChange={(event) => onChange(event.target.value)}
          style={{ width: 38, minHeight: 34, padding: 2, border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff" }}
        />
        <input value={textValue} onChange={(event) => onChange(event.target.value)} style={inputStyle} />
      </div>
    </label>
  );
}

const DocWriterImage = forwardRef(function DocWriterImage(
  {
    value,
    imageId = "",
    blockId = "",
    editable = true,
    readOnly = false,
    selected = false,
    maxWidth = 672,
    width,
    height,
    alignment,
    className = "",
    onChange,
    onDelete,
    onRequestDrag,
    onEditingChange,
    onSnapshot,
    onSnapshotReady,
    onImageSelected,
  },
  ref
) {
  const normalizedIncoming = useMemo(
    () => normalizeDocWriterImageValue(value, { width, height, alignment }),
    [value, width, height, alignment]
  );
  const [src, setSrc] = useState(normalizedIncoming.src);
  const [meta, setMeta] = useState(normalizedIncoming.meta);
  const [editOpen, setEditOpen] = useState(false);
  const [draftSrc, setDraftSrc] = useState(normalizedIncoming.src);
  const [draftMeta, setDraftMeta] = useState(normalizedIncoming.meta);
  const [resizeHover, setResizeHover] = useState("");
  const [saving, setSaving] = useState(false);
  const [fileError, setFileError] = useState("");
  const [draftUndoStack, setDraftUndoStack] = useState([]);
  const [draftRedoStack, setDraftRedoStack] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [activeSupplemental, setActiveSupplemental] = useState({ kind: "", id: "" });
  const [previewResizeHover, setPreviewResizeHover] = useState("");
  const pendingFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const printSurfaceRef = useRef(null);
  const rootRef = useRef(null);
  const resizeRef = useRef(null);
  const previewResizeRef = useRef(null);
  const moveRef = useRef(null);
  const draftStateRef = useRef({ src: normalizedIncoming.src, meta: { ...normalizedIncoming.meta }, pendingFile: null });
  const draftAspectRatioRef = useRef(Math.max(0.01, normalizedIncoming.meta.width / Math.max(1, normalizedIncoming.meta.height)));
  const incomingSignatureRef = useRef(imageStateSignature(normalizedIncoming.src, normalizedIncoming.meta));
  const DRAFT_HISTORY_LIMIT = 120;
  const actualImageId = imageId || blockId || "";
  const canEdit = Boolean(editable && !readOnly);

  const incomingSignature = useMemo(
    () => imageStateSignature(normalizedIncoming.src, normalizedIncoming.meta),
    [normalizedIncoming.src, normalizedIncoming.meta]
  );

  useEffect(() => {
    // Do not let the editOpen -> false transition restore stale props over a
    // just-saved local image. Only a genuinely new incoming value may replace
    // the live local value. If an external update arrives while the dialog is
    // open, it is intentionally applied after the dialog closes.
    if (editOpen) return;
    if (incomingSignature === incomingSignatureRef.current) return;
    incomingSignatureRef.current = incomingSignature;
    setSrc(normalizedIncoming.src);
    setMeta(normalizedIncoming.meta);
  }, [editOpen, incomingSignature, normalizedIncoming.src, normalizedIncoming.meta]);

  useEffect(() => {
    if (!editOpen) return;
    draftStateRef.current = {
      src: draftSrc,
      meta: { ...(draftMeta || {}) },
      pendingFile: pendingFileRef.current,
    };
  }, [editOpen, draftMeta, draftSrc]);

  const notifyEditing = useCallback((open, detail = {}) => {
    try {
      onEditingChange?.(Boolean(open), { blockId: actualImageId, imageId: actualImageId, kind: "image", ...detail });
    } catch (_) {}
  }, [actualImageId, onEditingChange]);

  const emitImageChange = useCallback((nextSrc, nextMeta, detail = {}) => {
    const cleanMeta = normalizeMeta(nextMeta);
    const value = serializeDocWriterImageValue(nextSrc, cleanMeta);
    const source = JSON.stringify(value);
    const handoff = {
      imageId: actualImageId,
      blockId: actualImageId,
      value,
      source,
      src: String(nextSrc || ""),
      meta: cleanMeta,
      width: cleanMeta.width,
      height: cleanMeta.height,
      alignment: cleanMeta.alignment,
      ...detail,
    };
    // Preserve the established Image callback contract (value, detail), while
    // making the first argument self-describing as well for newer Canvas glue.
    const outboundValue = {
      ...value,
      value,
      source,
      width: cleanMeta.width,
      height: cleanMeta.height,
      alignment: cleanMeta.alignment,
    };
    try { onChange?.(outboundValue, handoff); } catch (_) {}
    return { value, handoff };
  }, [actualImageId, onChange]);

  const serializeCurrent = useCallback((nextSrc = src, nextMeta = meta) => {
    return serializeDocWriterImageValue(nextSrc, nextMeta);
  }, [src, meta]);

  const emitSnapshot = useCallback(() => {
    const html = String(printSurfaceRef.current?.outerHTML || "").trim();
    if (!html) return "";
    const currentValue = serializeCurrent();
    const payload = {
      imageId: actualImageId,
      blockId: actualImageId,
      html,
      src,
      value: currentValue,
      source: JSON.stringify(currentValue),
      meta: { ...(currentValue.meta || {}) },
      width: safeNumber(meta.width, DEFAULT_WIDTH),
      height: safeNumber(meta.height, DEFAULT_HEIGHT),
      alignment: normalizeAlignment(meta.alignment),
    };
    try { onSnapshot?.(payload); } catch (_) {}
    try { onSnapshotReady?.(payload); } catch (_) {}
    return html;
  }, [actualImageId, meta, onSnapshot, onSnapshotReady, serializeCurrent, src]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame1 = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => emitSnapshot());
    });
    return () => window.cancelAnimationFrame(frame1);
  }, [emitSnapshot]);

  const resetDraftSession = useCallback((nextSrc = src, nextMeta = meta) => {
    const cleanMeta = { ...normalizeMeta(nextMeta) };
    pendingFileRef.current = null;
    draftStateRef.current = { src: nextSrc, meta: cleanMeta, pendingFile: null };
    setDraftSrc(nextSrc);
    setDraftMeta(cleanMeta);
    setDraftUndoStack([]);
    setDraftRedoStack([]);
    setDeleteConfirmOpen(false);
    setActiveSupplemental({ kind: "", id: "" });
    setPreviewResizeHover("");
    draftAspectRatioRef.current = Math.max(0.01, safeNumber(cleanMeta.width, DEFAULT_WIDTH) / Math.max(1, safeNumber(cleanMeta.height, DEFAULT_HEIGHT)));
  }, [meta, src]);

  const snapshotDraftState = useCallback(() => ({
    src: String(draftStateRef.current?.src || ""),
    meta: { ...(draftStateRef.current?.meta || {}) },
    pendingFile: draftStateRef.current?.pendingFile || pendingFileRef.current || null,
  }), []);

  const draftSnapshotSignature = useCallback((snapshot) => {
    try { return JSON.stringify({ src: snapshot?.src || "", meta: snapshot?.meta || {} }); } catch (_) { return String(Date.now()); }
  }, []);

  const captureDraftCheckpoint = useCallback(() => {
    const snapshot = snapshotDraftState();
    const signature = draftSnapshotSignature(snapshot);
    setDraftUndoStack((current) => {
      const last = current[current.length - 1];
      if (last && draftSnapshotSignature(last) === signature) return current;
      return [...current, snapshot].slice(-DRAFT_HISTORY_LIMIT);
    });
    setDraftRedoStack([]);
  }, [draftSnapshotSignature, snapshotDraftState]);

  const restoreDraftSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    const nextMeta = normalizeMeta(snapshot.meta || {});
    const nextSrc = String(snapshot.src || "");
    pendingFileRef.current = snapshot.pendingFile || null;
    draftStateRef.current = { src: nextSrc, meta: { ...nextMeta }, pendingFile: pendingFileRef.current };
    setDraftSrc(nextSrc);
    setDraftMeta(nextMeta);
    setFileError("");
    draftAspectRatioRef.current = Math.max(0.01, safeNumber(nextMeta.width, DEFAULT_WIDTH) / Math.max(1, safeNumber(nextMeta.height, DEFAULT_HEIGHT)));
  }, []);

  const undoDraftChange = useCallback(() => {
    setDraftUndoStack((current) => {
      if (!current.length) return current;
      const previous = current[current.length - 1];
      const now = snapshotDraftState();
      setDraftRedoStack((redo) => [...redo, now].slice(-DRAFT_HISTORY_LIMIT));
      restoreDraftSnapshot(previous);
      return current.slice(0, -1);
    });
  }, [restoreDraftSnapshot, snapshotDraftState]);

  const redoDraftChange = useCallback(() => {
    setDraftRedoStack((current) => {
      if (!current.length) return current;
      const next = current[current.length - 1];
      const now = snapshotDraftState();
      setDraftUndoStack((undo) => [...undo, now].slice(-DRAFT_HISTORY_LIMIT));
      restoreDraftSnapshot(next);
      return current.slice(0, -1);
    });
  }, [restoreDraftSnapshot, snapshotDraftState]);

  const updateDraftMeta = useCallback((updater, { history = true } = {}) => {
    const current = { ...(draftStateRef.current?.meta || {}) };
    const nextCandidate = typeof updater === "function" ? updater(current) : updater;
    const next = { ...(nextCandidate || current) };
    let changed = true;
    try { changed = JSON.stringify(next) !== JSON.stringify(current); } catch (_) {}
    if (!changed) return current;
    if (history) captureDraftCheckpoint();
    draftStateRef.current = { ...draftStateRef.current, meta: next };
    setDraftMeta(next);
    return next;
  }, [captureDraftCheckpoint]);

  const updateDraftSource = useCallback((nextSrc, pendingFile = null, { history = true } = {}) => {
    const resolved = String(nextSrc || "");
    if (resolved === String(draftStateRef.current?.src || "") && pendingFile === pendingFileRef.current) return;
    if (history) captureDraftCheckpoint();
    pendingFileRef.current = pendingFile;
    draftStateRef.current = { ...draftStateRef.current, src: resolved, pendingFile };
    setDraftSrc(resolved);
  }, [captureDraftCheckpoint]);

  const patchDraft = useCallback((key, nextValue, options = {}) => {
    updateDraftMeta((current) => ({ ...current, [key]: nextValue }), options);
  }, [updateDraftMeta]);

  useImperativeHandle(ref, () => ({
    openEditor: () => {
      if (!canEdit) return false;
      resetDraftSession(src, meta);
      setFileError("");
      setEditOpen(true);
      notifyEditing(true, { action: "edit" });
      return true;
    },
    closeEditor: () => {
      setEditOpen(false);
      setDeleteConfirmOpen(false);
      notifyEditing(false, { action: "edit" });
    },
    snapshot: emitSnapshot,
    getValue: () => serializeCurrent(),
  }), [canEdit, emitSnapshot, meta, notifyEditing, resetDraftSession, serializeCurrent, src]);

  const openEditor = useCallback(() => {
    if (!canEdit) return;
    resetDraftSession(src, meta);
    setFileError("");
    setEditOpen(true);
    notifyEditing(true, { action: "edit" });
  }, [canEdit, meta, notifyEditing, resetDraftSession, src]);

  const closeEditor = useCallback(() => {
    pendingFileRef.current = null;
    setEditOpen(false);
    setDeleteConfirmOpen(false);
    resetDraftSession(src, meta);
    setFileError("");
    notifyEditing(false, { action: "edit" });
  }, [meta, notifyEditing, resetDraftSession, src]);

  const normalizedDraftForPreview = useMemo(() => {
    const fallbackWidth = safeNumber(meta.width, DEFAULT_WIDTH);
    const fallbackHeight = safeNumber(meta.height, DEFAULT_HEIGHT);
    return normalizeMeta({
      ...draftMeta,
      width: draftMeta.width === "" ? fallbackWidth : draftMeta.width,
      height: draftMeta.height === "" ? fallbackHeight : draftMeta.height,
    });
  }, [draftMeta, meta.height, meta.width]);

  const validateDraftNumber = useCallback((key, minimum, maximum, fallback) => {
    updateDraftMeta((current) => {
      const raw = current[key];
      const next = clamp(safeNumber(raw, fallback), minimum, maximum);
      return { ...current, [key]: Math.round(next * 100) / 100 };
    });
  }, [updateDraftMeta]);

  const setDraftWidth = useCallback((raw) => {
    updateDraftMeta((current) => {
      if (raw === "") return { ...current, width: "" };
      const nextWidth = safeNumber(raw, safeNumber(current.width, DEFAULT_WIDTH));
      if (!boolValue(current.lockAspectRatio, true)) return { ...current, width: raw };
      const ratio = Math.max(0.01, safeNumber(draftAspectRatioRef.current, DEFAULT_WIDTH / DEFAULT_HEIGHT));
      return { ...current, width: raw, height: Math.max(MIN_HEIGHT, Math.round(nextWidth / ratio)) };
    });
  }, [updateDraftMeta]);

  const setDraftHeight = useCallback((raw) => {
    updateDraftMeta((current) => {
      if (raw === "") return { ...current, height: "" };
      const nextHeight = safeNumber(raw, safeNumber(current.height, DEFAULT_HEIGHT));
      if (!boolValue(current.lockAspectRatio, true)) return { ...current, height: raw };
      const ratio = Math.max(0.01, safeNumber(draftAspectRatioRef.current, DEFAULT_WIDTH / DEFAULT_HEIGHT));
      return { ...current, height: raw, width: Math.max(MIN_WIDTH, Math.round(nextHeight * ratio)) };
    });
  }, [updateDraftMeta]);

  const applySizePreset = useCallback((presetKey) => {
    const preset = SIZE_PRESETS[presetKey] || SIZE_PRESETS.medium;
    const targetWidth = preset.width == null ? Math.max(MIN_WIDTH, Math.min(maxWidth || 672, MAX_WIDTH)) : preset.width;
    updateDraftMeta((current) => {
      const aspect = Math.max(0.01, safeNumber(draftAspectRatioRef.current, DEFAULT_WIDTH / DEFAULT_HEIGHT));
      return {
        ...current,
        width: Math.round(targetWidth),
        height: boolValue(current.lockAspectRatio, true)
          ? Math.max(MIN_HEIGHT, Math.round(targetWidth / aspect))
          : current.height,
      };
    });
  }, [maxWidth, updateDraftMeta]);

  const chooseFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.has(file.type)) {
      setFileError("Use JPEG/JPG, PNG, WebP, or GIF image files.");
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setFileError("The selected image is larger than 30 MB.");
      return;
    }

    setFileError("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const dimensions = await loadImageDimensions(dataUrl);
      captureDraftCheckpoint();
      pendingFileRef.current = file;
      draftStateRef.current = { ...draftStateRef.current, src: dataUrl, pendingFile: file };
      setDraftSrc(dataUrl);
      updateDraftMeta((current) => {
        const next = {
          ...current,
          fileName: file.name || "",
          mimeType: file.type || "",
          naturalWidth: dimensions.width || 0,
          naturalHeight: dimensions.height || 0,
        };
        if (dimensions.width > 0 && dimensions.height > 0) {
          draftAspectRatioRef.current = Math.max(0.01, dimensions.width / dimensions.height);
          const targetWidth = Math.min(maxWidth || 672, dimensions.width, DEFAULT_WIDTH);
          next.width = Math.max(MIN_WIDTH, Math.round(targetWidth));
          next.height = Math.max(MIN_HEIGHT, Math.round((targetWidth / dimensions.width) * dimensions.height));
        }
        return next;
      }, { history: false });
    } catch (_) {
      setFileError("The image could not be read.");
    }
  }, [captureDraftCheckpoint, maxWidth, updateDraftMeta]);

  const collectLatestDraftMeta = useCallback(() => {
    let nextMeta = normalizeMeta(draftStateRef.current?.meta || draftMeta || {});
    if (typeof document !== "undefined") {
      const collect = (kind, key) => {
        const lines = normalizeImageSupplementalLines(nextMeta[key], kind);
        if (!lines.length) return;
        const nextLines = lines.map((line) => {
          const selector = `.doc-writer-image-edit-overlay [data-docwriter-image-supplemental-kind="${kind}"][data-docwriter-image-supplemental-id="${line.id}"]`;
          const node = document.querySelector(selector);
          return node ? { ...line, text: String(node.textContent || "") } : line;
        });
        nextMeta = { ...nextMeta, [key]: nextLines };
      };
      collect("title", "topTitles");
      collect("note", "bottomNotes");
    }
    draftStateRef.current = { ...draftStateRef.current, meta: { ...nextMeta } };
    return nextMeta;
  }, [draftMeta]);

  const saveEditor = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const latestDraftMeta = collectLatestDraftMeta();
    let finalSrc = String(draftStateRef.current?.src || draftSrc || "");
    const pendingFile = draftStateRef.current?.pendingFile || pendingFileRef.current;
    try {
      if (pendingFile && typeof onImageSelected === "function") {
        const result = await onImageSelected(pendingFile, {
          dataUrl: finalSrc,
          imageId: actualImageId,
          meta: latestDraftMeta,
        });
        if (typeof result === "string" && result.trim()) {
          finalSrc = result.trim();
        } else if (result && typeof result === "object") {
          finalSrc = String(result.src || result.url || result.dataUrl || finalSrc);
        }
      }

      const nextMeta = normalizeMeta(draftStateRef.current?.meta || latestDraftMeta);
      setSrc(finalSrc);
      setMeta(nextMeta);
      setDraftSrc(finalSrc);
      setDraftMeta(nextMeta);
      draftStateRef.current = { src: finalSrc, meta: { ...nextMeta }, pendingFile: null };
      pendingFileRef.current = null;
      setDraftUndoStack([]);
      setDraftRedoStack([]);
      setDeleteConfirmOpen(false);
      emitImageChange(finalSrc, nextMeta, { reason: "edit" });
      setEditOpen(false);
      notifyEditing(false, { action: "edit", saved: true });
    } finally {
      setSaving(false);
    }
  }, [actualImageId, collectLatestDraftMeta, draftSrc, emitImageChange, notifyEditing, onImageSelected, saving]);

  const deleteImage = useCallback(() => {
    setDeleteConfirmOpen(false);
    closeEditor();
    try { onDelete?.(serializeCurrent()); } catch (_) {}
  }, [closeEditor, onDelete, serializeCurrent]);

  const beginResize = useCallback((event, handle = "se") => {
    if (!canEdit || !event || event.button !== 0 || resizeRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const startWidth = safeNumber(meta.width, DEFAULT_WIDTH);
    const startHeight = safeNumber(meta.height, DEFAULT_HEIGHT);
    const ratio = startWidth / Math.max(1, startHeight);
    const locked = boolValue(meta.lockAspectRatio, true);
    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    resizeRef.current = {
      pointerId: event.pointerId,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      ratio,
      locked,
      latest: { width: startWidth, height: startHeight },
    };
    setResizeHover(handle);
    notifyEditing(true, { action: "resize", handle });
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const state = resizeRef.current;
      if (!state) return;
      const dx = moveEvent.clientX - state.startX;
      const dy = moveEvent.clientY - state.startY;
      let nextWidth = state.startWidth;
      let nextHeight = state.startHeight;
      const maxAllowedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, maxWidth || MAX_WIDTH));

      if (state.locked) {
        let desiredScale = 1;
        if (resizeHorizontal) {
          const widthCandidate = state.startWidth + (resizeFromWest ? -dx : dx);
          const heightCandidate = state.startHeight + (resizeFromNorth ? -dy : dy);
          const sx = widthCandidate / Math.max(1, state.startWidth);
          const sy = heightCandidate / Math.max(1, state.startHeight);
          desiredScale = Math.abs(sx - 1) >= Math.abs(sy - 1) ? sx : sy;
        } else {
          const heightCandidate = state.startHeight + (resizeFromNorth ? -dy : dy);
          desiredScale = heightCandidate / Math.max(1, state.startHeight);
        }
        const minScale = Math.max(MIN_WIDTH / state.startWidth, MIN_HEIGHT / state.startHeight);
        const maxScale = Math.min(maxAllowedWidth / state.startWidth, MAX_HEIGHT / state.startHeight);
        const scale = clamp(desiredScale, minScale, maxScale);
        nextWidth = Math.round(state.startWidth * scale);
        nextHeight = Math.round(state.startHeight * scale);
      } else {
        if (resizeHorizontal) {
          nextWidth = clamp(state.startWidth + (resizeFromWest ? -dx : dx), MIN_WIDTH, maxAllowedWidth);
        }
        nextHeight = clamp(state.startHeight + (resizeFromNorth ? -dy : dy), MIN_HEIGHT, MAX_HEIGHT);
      }

      const next = { width: Math.round(nextWidth), height: Math.round(nextHeight) };
      state.latest = next;
      setMeta((current) => ({ ...current, ...next }));
    };

    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      const state = resizeRef.current;
      resizeRef.current = null;
      setResizeHover("");
      if (!state) return;
      const finalSize = state.latest || { width: state.startWidth, height: state.startHeight };
      setMeta((current) => {
        const nextMeta = normalizeMeta({ ...current, ...finalSize });
        emitImageChange(src, nextMeta, {
          reason: "resize",
          handle: state.handle,
        });
        return nextMeta;
      });
      notifyEditing(false, { action: "resize", saved: true, handle: state.handle });
    };

    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  }, [canEdit, emitImageChange, maxWidth, meta.lockAspectRatio, meta.height, meta.width, notifyEditing, src]);

  const topTitles = normalizeImageSupplementalLines(draftMeta.topTitles, "title");
  const bottomNotes = normalizeImageSupplementalLines(draftMeta.bottomNotes, "note");
  const activeLines = activeSupplemental.kind === "note" ? bottomNotes : topTitles;
  const activeSupplementalLine = activeLines.find((line) => line.id === activeSupplemental.id) || null;

  const updateSupplementalLine = useCallback((kind, lineId, linePatch, options = {}) => {
    const key = kind === "note" ? "bottomNotes" : "topTitles";
    updateDraftMeta((current) => {
      const lines = normalizeImageSupplementalLines(current[key], kind);
      const next = lines.map((line) => line.id === lineId ? normalizeImageSupplementalLine({ ...line, ...(linePatch || {}) }, kind, 0) : line);
      return { ...current, [key]: next };
    }, options);
  }, [updateDraftMeta]);

  // Direct title/note typing is intentionally recorded one input at a time.
  // Keep the contentEditable DOM under the user's caret while typing; update
  // the draft ref/history without forcing a React text rerender on every key.
  const recordSupplementalTyping = useCallback((kind, lineId, nextText) => {
    const key = kind === "note" ? "bottomNotes" : "topTitles";
    const currentMeta = { ...(draftStateRef.current?.meta || {}) };
    const lines = normalizeImageSupplementalLines(currentMeta[key], kind);
    const resolvedText = String(nextText || "");
    let changed = false;
    const nextLines = lines.map((line) => {
      if (line.id !== lineId || line.text === resolvedText) return line;
      changed = true;
      return { ...line, text: resolvedText };
    });
    if (!changed) return;
    captureDraftCheckpoint();
    draftStateRef.current = {
      ...draftStateRef.current,
      meta: { ...currentMeta, [key]: nextLines },
    };
  }, [captureDraftCheckpoint]);

  const focusSupplementalPreviewLine = useCallback((kind, lineId) => {
    const selector = `[data-docwriter-image-supplemental-kind="${kind}"][data-docwriter-image-supplemental-id="${lineId}"]`;
    const focusLine = () => {
      const node = document?.querySelector?.(selector);
      if (!node) return false;
      try { node.scrollIntoView?.({ block: "nearest", inline: "nearest" }); } catch (_) {}
      try { node.focus?.({ preventScroll: true }); } catch (_) { try { node.focus?.(); } catch (_) {} }
      try {
        const selection = document?.getSelection?.();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(node);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } catch (_) {}
      return true;
    };
    const view = typeof window !== "undefined" ? window : null;
    const firstFrame = () => {
      if (focusLine()) return;
      try { view?.requestAnimationFrame?.(() => { if (!focusLine()) view?.setTimeout?.(focusLine, 0); }); } catch (_) { try { view?.setTimeout?.(focusLine, 0); } catch (_) {} }
    };
    try { view?.requestAnimationFrame?.(firstFrame); } catch (_) { try { view?.setTimeout?.(firstFrame, 0); } catch (_) {} }
  }, []);

  const addSupplementalLine = useCallback((kind) => {
    const key = kind === "note" ? "bottomNotes" : "topTitles";
    const lines = normalizeImageSupplementalLines(draftStateRef.current?.meta?.[key], kind);
    const nextLine = normalizeImageSupplementalLine({
      id: createImageSupplementalId(kind === "note" ? "noteline" : "titleline"),
      text: "",
      alignment: "center",
    }, kind, lines.length);
    updateDraftMeta((current) => ({ ...current, [key]: [...lines, nextLine] }));
    setActiveSupplemental({ kind, id: nextLine.id });
    focusSupplementalPreviewLine(kind, nextLine.id);
  }, [focusSupplementalPreviewLine, updateDraftMeta]);

  const removeSupplementalLine = useCallback((kind) => {
    const key = kind === "note" ? "bottomNotes" : "topTitles";
    const lines = normalizeImageSupplementalLines(draftStateRef.current?.meta?.[key], kind);
    if (!lines.length) return;
    const activeMatches = activeSupplemental.kind === kind && lines.some((line) => line.id === activeSupplemental.id);
    const targetId = activeMatches ? activeSupplemental.id : lines[lines.length - 1].id;
    updateDraftMeta((current) => ({ ...current, [key]: lines.filter((line) => line.id !== targetId) }));
    setActiveSupplemental({ kind: "", id: "" });
  }, [activeSupplemental, updateDraftMeta]);

  const commitLiveSupplementalText = useCallback((kind, lineId, nextText) => {
    const key = kind === "note" ? "bottomNotes" : "topTitles";
    const lines = normalizeImageSupplementalLines(meta[key], kind);
    let changed = false;
    const nextLines = lines.map((line) => {
      if (line.id !== lineId || line.text === String(nextText || "")) return line;
      changed = true;
      return { ...line, text: String(nextText || "") };
    });
    if (!changed) return;
    const nextMeta = normalizeMeta({ ...meta, [key]: nextLines });
    setMeta(nextMeta);
    emitImageChange(src, nextMeta, { reason: "supplemental-text" });
  }, [emitImageChange, meta, src]);

  const previewResizeHandles = [
    { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
    { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
    { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
    { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
  ];
  const previewEdgeHandles = [
    { handle: "n", edge: "top", cursor: "ns-resize" },
    { handle: "s", edge: "bottom", cursor: "ns-resize" },
  ];

  const beginPreviewResize = useCallback((event, handle = "se") => {
    if (!event || event.button !== 0 || previewResizeRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const currentMeta = normalizeMeta(draftStateRef.current?.meta || draftMeta);
    const startWidth = currentMeta.width;
    const startHeight = currentMeta.height;
    const locked = boolValue(currentMeta.lockAspectRatio, true);
    const resizeHorizontal = handle.includes("e") || handle.includes("w");
    const resizeFromWest = handle.includes("w");
    const resizeFromNorth = handle.includes("n");
    captureDraftCheckpoint();
    previewResizeRef.current = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth,
      startHeight,
      locked,
    };
    setPreviewResizeHover(handle);
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) {}

    const move = (moveEvent) => {
      const state = previewResizeRef.current;
      if (!state) return;
      const dx = moveEvent.clientX - state.startX;
      const dy = moveEvent.clientY - state.startY;
      const maxAllowedWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, maxWidth || MAX_WIDTH));
      let nextWidth = state.startWidth;
      let nextHeight = state.startHeight;
      if (state.locked) {
        let desiredScale = 1;
        if (resizeHorizontal) {
          const widthCandidate = state.startWidth + (resizeFromWest ? -dx : dx);
          const heightCandidate = state.startHeight + (resizeFromNorth ? -dy : dy);
          const sx = widthCandidate / Math.max(1, state.startWidth);
          const sy = heightCandidate / Math.max(1, state.startHeight);
          desiredScale = Math.abs(sx - 1) >= Math.abs(sy - 1) ? sx : sy;
        } else {
          desiredScale = (state.startHeight + (resizeFromNorth ? -dy : dy)) / Math.max(1, state.startHeight);
        }
        const minScale = Math.max(MIN_WIDTH / state.startWidth, MIN_HEIGHT / state.startHeight);
        const maxScale = Math.min(maxAllowedWidth / state.startWidth, MAX_HEIGHT / state.startHeight);
        const scale = clamp(desiredScale, minScale, maxScale);
        nextWidth = Math.round(state.startWidth * scale);
        nextHeight = Math.round(state.startHeight * scale);
      } else {
        if (resizeHorizontal) nextWidth = clamp(state.startWidth + (resizeFromWest ? -dx : dx), MIN_WIDTH, maxAllowedWidth);
        nextHeight = clamp(state.startHeight + (resizeFromNorth ? -dy : dy), MIN_HEIGHT, MAX_HEIGHT);
      }
      updateDraftMeta((current) => ({ ...current, width: Math.round(nextWidth), height: Math.round(nextHeight) }), { history: false });
    };

    const finish = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", finish, true);
      window.removeEventListener("pointercancel", finish, true);
      previewResizeRef.current = null;
      setPreviewResizeHover("");
    };
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", finish, true);
    window.addEventListener("pointercancel", finish, true);
  }, [captureDraftCheckpoint, draftMeta, maxWidth, updateDraftMeta]);


  const shellAlignment = normalizeAlignment(meta.alignment);

  const buttonStyle = {
    minHeight: 32,
    padding: "7px 11px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#ffffff",
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 3px 10px rgba(15,23,42,0.08)",
  };

  const inputStyle = {
    width: "100%",
    minHeight: 34,
    boxSizing: "border-box",
    padding: "7px 9px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#ffffff",
    color: TEXT_DARK,
    fontSize: 12,
    outline: "none",
  };

  const labelStyle = {
    minWidth: 0,
    display: "grid",
    gap: 5,
    color: TEXT_MUTED,
    fontSize: 11,
    fontWeight: 800,
  };

  const checkStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: 700,
  };

  const sectionStyle = {
    padding: 12,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#ffffff",
  };

  const sectionTitleStyle = {
    marginBottom: 9,
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: 900,
  };

  const rowGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 9,
  };

  const numberField = ({ label, keyName, min, max, fallback, step = 1, setter = null }) => (
    <label style={labelStyle}>
      {label}
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={draftMeta[keyName] ?? ""}
        onChange={(event) => {
          const raw = event.target.value;
          if (setter) setter(raw);
          else patchDraft(keyName, raw);
        }}
        onBlur={() => validateDraftNumber(keyName, min, max, fallback)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            validateDraftNumber(keyName, min, max, fallback);
            event.currentTarget.blur();
          }
        }}
        style={inputStyle}
      />
    </label>
  );

  const colorField = (label, keyName, fallback) => (
    <ColorField
      label={label}
      value={draftMeta[keyName]}
      fallback={fallback}
      onChange={(next) => patchDraft(keyName, next)}
      inputStyle={inputStyle}
      labelStyle={labelStyle}
    />
  );

  const editDialog = canEdit && editOpen && typeof document !== "undefined"
    ? createPortal(
      <div
        className="doc-writer-image-edit-overlay"
        {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          const accelerator = event.ctrlKey || event.metaKey;
          const key = String(event.key || "").toLowerCase();
          if (accelerator && key === "z") {
            event.preventDefault();
            if (event.shiftKey) redoDraftChange(); else undoDraftChange();
            return;
          }
          if (accelerator && key === "y") {
            event.preventDefault();
            redoDraftChange();
            return;
          }
          if (event.key === "Escape") {
            event.preventDefault();
            if (deleteConfirmOpen) setDeleteConfirmOpen(false); else closeEditor();
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483000,
          display: "grid",
          placeItems: "center",
          padding: 20,
          boxSizing: "border-box",
          background: "rgba(15,23,42,0.30)",
        }}
      >
        <style>{`
          @media (max-width: 920px) {
            .doc-writer-image-edit-two-pane { grid-template-columns: minmax(0, 1fr) !important; }
            .doc-writer-image-edit-preview-pane { position: relative !important; top: auto !important; }
          }
        `}</style>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Edit image"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(1180px, 96vw)",
            maxHeight: "90vh",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: PANEL_BG,
            borderRadius: 18,
            boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
            border: `1px solid ${BORDER}`,
            color: TEXT_DARK,
            fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "17px 18px 15px", borderBottom: "1px solid rgba(148,163,184,0.22)", background: "#ffffff" }}>
            <div>
              <div style={{ fontWeight: 900, color: TEXT_DARK, fontSize: 16 }}>Edit image</div>
              <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>Edit the live preview freely. The document image changes only when you choose Save changes.</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "0 0 auto" }}>
              <button type="button" style={{ ...buttonStyle, minWidth: 66, opacity: draftUndoStack.length ? 1 : 0.48, cursor: draftUndoStack.length ? "pointer" : "default" }} disabled={!draftUndoStack.length} onClick={undoDraftChange} title="Undo the last unsaved image-preview change">Undo</button>
              <button type="button" style={{ ...buttonStyle, minWidth: 66, opacity: draftRedoStack.length ? 1 : 0.48, cursor: draftRedoStack.length ? "pointer" : "default" }} disabled={!draftRedoStack.length} onClick={redoDraftChange} title="Redo the last unsaved image-preview change">Redo</button>
              <button type="button" style={buttonStyle} onClick={closeEditor}>Close</button>
            </div>
          </div>

          <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarGutter: "stable", padding: "16px 18px 18px", background: "#ffffff" }}>
            <div className="doc-writer-image-edit-two-pane" style={{ display: "grid", gridTemplateColumns: "minmax(330px, 410px) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
              <div style={{ minWidth: 0, display: "grid", gap: 12 }}>
                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Image source</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    onChange={chooseFile}
                    style={{ display: "none" }}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button type="button" style={{ ...buttonStyle, color: ACCENT }} onClick={() => fileInputRef.current?.click()}>{draftSrc ? "Replace image" : "Choose image"}</button>
                    {draftSrc ? <button type="button" style={buttonStyle} onClick={() => updateDraftSource("", null)}>Remove from preview</button> : null}
                  </div>
                  {fileError ? <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 11, fontWeight: 700 }}>{fileError}</div> : null}
                  <div style={{ marginTop: 9, color: TEXT_MUTED, fontSize: 11, lineHeight: 1.45 }}>JPEG/JPG, PNG, WebP, and GIF. The parent Editor can provide <code>onImageSelected</code> later to upload the file and replace the preview data URL with a permanent stored URL.</div>
                  {draftMeta.fileName ? <div style={{ marginTop: 7, fontSize: 11, color: TEXT_MUTED, overflowWrap: "anywhere" }}>File: <strong>{draftMeta.fileName}</strong>{draftMeta.mimeType ? ` · ${draftMeta.mimeType}` : ""}</div> : null}
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Top titles and bottom notes</div>
                  <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45, marginBottom: 10 }}>
                    Add as many independent lines as needed. Click a title/note line in the preview to make it active, then use the formatting controls below. These remain draft-only until Save changes.
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button type="button" style={buttonStyle} onClick={() => addSupplementalLine("title")}>Add top title line</button>
                    <button type="button" style={buttonStyle} onClick={() => removeSupplementalLine("title")}>Remove active top line</button>
                    <button type="button" style={buttonStyle} onClick={() => addSupplementalLine("note")}>Add bottom note line</button>
                    <button type="button" style={buttonStyle} onClick={() => removeSupplementalLine("note")}>Remove active bottom line</button>
                  </div>
                  <div style={{ marginTop: 9, fontSize: 11, color: TEXT_MUTED }}>Click a top title or bottom note line in the preview to format it.</div>
                  {activeSupplementalLine ? (
                    <div style={{ ...rowGrid, marginTop: 10 }}>
                      <label style={labelStyle}>
                        Active line alignment
                        <select value={activeSupplementalLine.alignment} onChange={(event) => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { alignment: event.target.value })} style={inputStyle}>
                          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Active line font size (px)
                        <input type="number" min="3" max="96" value={activeSupplementalLine.fontSizePx} onChange={(event) => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { fontSizePx: clamp(safeNumber(event.target.value, activeSupplementalLine.fontSizePx), 3, 96) })} style={inputStyle} />
                      </label>
                      <div style={{ display: "flex", alignItems: "end", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.bold ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { bold: !activeSupplementalLine.bold })}>Bold</button>
                        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.italic ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { italic: !activeSupplementalLine.italic })}>Italic</button>
                        <button type="button" style={{ ...buttonStyle, background: activeSupplementalLine.underline ? "#eef2ff" : "#ffffff" }} onClick={() => updateSupplementalLine(activeSupplemental.kind, activeSupplementalLine.id, { underline: !activeSupplementalLine.underline })}>Underline</button>
                      </div>
                    </div>
                  ) : null}
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Size and placement</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
                    {Object.entries(SIZE_PRESETS).map(([key, preset]) => (
                      <button key={key} type="button" style={buttonStyle} onClick={() => applySizePreset(key)}>{preset.label}</button>
                    ))}
                  </div>
                  <div style={rowGrid}>
                    {numberField({ label: "Width (px)", keyName: "width", min: MIN_WIDTH, max: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, maxWidth || MAX_WIDTH)), fallback: safeNumber(meta.width, DEFAULT_WIDTH), setter: setDraftWidth })}
                    {numberField({ label: "Height (px)", keyName: "height", min: MIN_HEIGHT, max: MAX_HEIGHT, fallback: safeNumber(meta.height, DEFAULT_HEIGHT), setter: setDraftHeight })}
                    <label style={labelStyle}>
                      Alignment
                      <select value={normalizeAlignment(draftMeta.alignment)} onChange={(event) => patchDraft("alignment", event.target.value)} style={inputStyle}>
                        <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                      </select>
                    </label>
                    <label style={labelStyle}>
                      Image fit
                      <select value={normalizeFit(draftMeta.fit)} onChange={(event) => patchDraft("fit", event.target.value)} style={inputStyle}>
                        <option value="contain">Contain</option>
                        <option value="cover">Cover / crop</option>
                        <option value="fill">Stretch to frame</option>
                        <option value="scale-down">Scale down</option>
                        <option value="none">Natural size</option>
                      </select>
                    </label>
                  </div>
                  <label style={{ ...checkStyle, marginTop: 10 }}><input type="checkbox" checked={boolValue(draftMeta.lockAspectRatio, true)} onChange={(event) => patchDraft("lockAspectRatio", event.target.checked)} />Lock aspect ratio</label>
                  <div style={{ marginTop: 9, color: TEXT_MUTED, fontSize: 11, fontWeight: 800 }}>Frame ratio</div>
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {[
                      ["Original", safeNumber(draftMeta.naturalWidth, 0) > 0 && safeNumber(draftMeta.naturalHeight, 0) > 0 ? safeNumber(draftMeta.naturalWidth, 1) / Math.max(1, safeNumber(draftMeta.naturalHeight, 1)) : draftAspectRatioRef.current],
                      ["1:1", 1], ["4:3", 4 / 3], ["3:2", 3 / 2], ["16:9", 16 / 9],
                    ].map(([label, ratio]) => (
                      <button key={label} type="button" style={buttonStyle} onClick={() => {
                        const nextRatio = Math.max(0.01, safeNumber(ratio, DEFAULT_WIDTH / DEFAULT_HEIGHT));
                        draftAspectRatioRef.current = nextRatio;
                        updateDraftMeta((current) => {
                          const currentWidth = clamp(safeNumber(current.width, DEFAULT_WIDTH), MIN_WIDTH, Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, maxWidth || MAX_WIDTH)));
                          return { ...current, lockAspectRatio: true, width: currentWidth, height: clamp(Math.round(currentWidth / nextRatio), MIN_HEIGHT, MAX_HEIGHT) };
                        });
                      }}>{label}</button>
                    ))}
                  </div>
                  <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button type="button" style={buttonStyle} onClick={() => updateDraftMeta((current) => ({ ...current, width: meta.width, height: meta.height }))}>Reset size</button>
                    {safeNumber(draftMeta.naturalWidth, 0) > 0 && safeNumber(draftMeta.naturalHeight, 0) > 0 ? (
                      <button type="button" style={buttonStyle} onClick={() => {
                        const naturalWidth = safeNumber(draftMeta.naturalWidth, DEFAULT_WIDTH);
                        const naturalHeight = safeNumber(draftMeta.naturalHeight, DEFAULT_HEIGHT);
                        const targetWidth = Math.min(naturalWidth, maxWidth || naturalWidth, MAX_WIDTH);
                        updateDraftMeta((current) => ({ ...current, width: Math.round(targetWidth), height: Math.round((targetWidth / naturalWidth) * naturalHeight) }));
                      }}>Use natural ratio</button>
                    ) : null}
                  </div>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Crop, position and transform</div>
                  <div style={rowGrid}>
                    {numberField({ label: "Horizontal position (%)", keyName: "positionX", min: 0, max: 100, fallback: 50 })}
                    {numberField({ label: "Vertical position (%)", keyName: "positionY", min: 0, max: 100, fallback: 50 })}
                    {numberField({ label: "Rotation (°)", keyName: "rotation", min: -180, max: 180, fallback: 0 })}
                    {numberField({ label: "Image zoom (%)", keyName: "zoom", min: 25, max: 400, fallback: 100, step: 5 })}
                    <div style={{ display: "grid", gap: 6 }}>
                      <span style={{ color: TEXT_MUTED, fontSize: 11, fontWeight: 800 }}>Quick rotate</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button type="button" style={buttonStyle} onClick={() => patchDraft("rotation", clamp(safeNumber(draftMeta.rotation, 0) - 90, -180, 180))}>↶ 90°</button>
                        <button type="button" style={buttonStyle} onClick={() => patchDraft("rotation", clamp(safeNumber(draftMeta.rotation, 0) + 90, -180, 180))}>↷ 90°</button>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10 }}>
                    <label style={checkStyle}><input type="checkbox" checked={boolValue(draftMeta.flipX, false)} onChange={(event) => patchDraft("flipX", event.target.checked)} />Flip horizontal</label>
                    <label style={checkStyle}><input type="checkbox" checked={boolValue(draftMeta.flipY, false)} onChange={(event) => patchDraft("flipY", event.target.checked)} />Flip vertical</label>
                  </div>
                  <div style={{ marginTop: 9, display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <button type="button" style={buttonStyle} onClick={() => updateDraftMeta((current) => ({ ...current, positionX: 50, positionY: 50 }))}>Reset image position</button>
                    <button type="button" style={buttonStyle} onClick={() => updateDraftMeta((current) => ({ ...current, rotation: 0, flipX: false, flipY: false, zoom: 100 }))}>Reset transform + zoom</button>
                  </div>
                  <div style={{ marginTop: 7, color: TEXT_MUTED, fontSize: 11 }}>You can also drag the image directly inside the live preview to adjust its horizontal/vertical position within the frame.</div>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Appearance</div>
                  <div style={rowGrid}>
                    {colorField("Frame background", "background", "#ffffff")}
                    {colorField("Border color", "borderColor", "#cbd5e1")}
                    {numberField({ label: "Border width (px)", keyName: "borderWidth", min: 0, max: 20, fallback: 0 })}
                    <label style={labelStyle}>Border style<select value={normalizeBorderStyle(draftMeta.borderStyle)} onChange={(event) => patchDraft("borderStyle", event.target.value)} style={inputStyle}><option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option><option value="double">Double</option><option value="none">None</option></select></label>
                    {numberField({ label: "Corner radius (px)", keyName: "borderRadius", min: 0, max: 100, fallback: 0 })}
                    {numberField({ label: "Inner padding (px)", keyName: "padding", min: 0, max: 80, fallback: 0 })}
                    <label style={labelStyle}>Shadow<select value={normalizeShadow(draftMeta.shadow)} onChange={(event) => patchDraft("shadow", event.target.value)} style={inputStyle}><option value="none">None</option><option value="soft">Soft</option><option value="strong">Strong</option></select></label>
                    {numberField({ label: "Opacity (%)", keyName: "opacity", min: 0, max: 100, fallback: 100 })}
                  </div>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Image adjustments</div>
                  <div style={rowGrid}>
                    {numberField({ label: "Brightness (%)", keyName: "brightness", min: 0, max: 300, fallback: 100 })}
                    {numberField({ label: "Contrast (%)", keyName: "contrast", min: 0, max: 300, fallback: 100 })}
                    {numberField({ label: "Saturation (%)", keyName: "saturation", min: 0, max: 300, fallback: 100 })}
                    {numberField({ label: "Grayscale (%)", keyName: "grayscale", min: 0, max: 100, fallback: 0 })}
                    {numberField({ label: "Sepia (%)", keyName: "sepia", min: 0, max: 100, fallback: 0 })}
                    {numberField({ label: "Blur (px)", keyName: "blur", min: 0, max: 20, fallback: 0, step: 0.5 })}
                  </div>
                  <button type="button" style={{ ...buttonStyle, marginTop: 9 }} onClick={() => updateDraftMeta((current) => ({ ...current, opacity: 100, brightness: 100, contrast: 100, saturation: 100, grayscale: 0, sepia: 0, blur: 0 }))}>Reset image adjustments</button>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Caption</div>
                  <label style={checkStyle}><input type="checkbox" checked={boolValue(draftMeta.showCaption, false)} onChange={(event) => patchDraft("showCaption", event.target.checked)} />Show caption</label>
                  <label style={{ ...labelStyle, marginTop: 9 }}>
                    Caption text
                    <textarea value={draftMeta.caption || ""} onChange={(event) => patchDraft("caption", event.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical", minHeight: 72 }} placeholder="Optional image caption" />
                  </label>
                  <div style={{ ...rowGrid, marginTop: 9 }}>
                    <label style={labelStyle}>Caption alignment<select value={normalizeAlignment(draftMeta.captionAlignment)} onChange={(event) => patchDraft("captionAlignment", event.target.value)} style={inputStyle}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
                    {numberField({ label: "Caption font size", keyName: "captionFontSize", min: 7, max: 40, fallback: 12 })}
                    {colorField("Caption text color", "captionColor", TEXT_MUTED)}
                    {colorField("Caption background", "captionBackground", "#ffffff")}
                  </div>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Accessibility</div>
                  <label style={labelStyle}>
                    Alt text
                    <textarea value={draftMeta.altText || ""} onChange={(event) => patchDraft("altText", event.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical", minHeight: 72 }} placeholder="Describe the image for accessibility" />
                  </label>
                </section>

                <section style={sectionStyle}>
                  <div style={sectionTitleStyle}>Reset</div>
                  <button type="button" style={buttonStyle} onClick={() => updateDraftMeta((current) => ({
                    ...defaultMeta(),
                    naturalWidth: current.naturalWidth,
                    naturalHeight: current.naturalHeight,
                    fileName: current.fileName,
                    mimeType: current.mimeType,
                    altText: current.altText,
                    caption: current.caption,
                    topTitles: current.topTitles,
                    bottomNotes: current.bottomNotes,
                  }))}>Reset presentation controls</button>
                </section>
              </div>

              <div className="doc-writer-image-edit-preview-pane" style={{ minWidth: 0, position: "sticky", top: 0, alignSelf: "start" }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: TEXT_DARK, marginBottom: 6 }}>Live preview</div>
                <div style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45, marginBottom: 9 }}>Drag the image itself to reposition it inside the frame. The preview has its own right and bottom scrollbars whenever the image needs more space.</div>
                <div
                  className="doc-writer-image-live-preview-scroll"
                  style={{
                    position: "relative",
                    maxHeight: "min(64vh, 650px)",
                    minHeight: 320,
                    overflowY: "auto",
                    overflowX: "auto",
                    scrollbarGutter: "stable",
                    padding: "18px 28px 38px 18px",
                    border: "1px solid #dbe3ee",
                    borderRadius: 12,
                    background: SOFT_BG,
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ minWidth: "100%", width: "max-content", minHeight: 260, display: "flex", justifyContent: normalizeAlignment(normalizedDraftForPreview.alignment) === "left" ? "flex-start" : normalizeAlignment(normalizedDraftForPreview.alignment) === "right" ? "flex-end" : "center", alignItems: "flex-start", padding: 14, boxSizing: "border-box", background: "#ffffff", borderRadius: 10 }}>
                    <div style={{ position: "relative", width: `${safeNumber(normalizedDraftForPreview.width, DEFAULT_WIDTH)}px`, maxWidth: "none", boxSizing: "border-box" }}>
                      {previewResizeHandles.map((handle) => {
                        const hovered = previewResizeHover === handle.handle;
                        return (
                          <div
                            key={`image-preview-resize-${handle.handle}`}
                            role="button"
                            tabIndex={-1}
                            {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
                            data-docwriter-image-preview-resize-corner={handle.handle}
                            aria-label={`Resize preview image from ${handle.vertical} ${handle.horizontal} corner`}
                            title={boolValue(normalizedDraftForPreview.lockAspectRatio, true) ? "Resize image proportionally" : "Resize image"}
                            onPointerEnter={() => setPreviewResizeHover(handle.handle)}
                            onPointerLeave={() => setPreviewResizeHover("")}
                            onPointerDown={(event) => beginPreviewResize(event, handle.handle)}
                            style={{
                              position: "absolute",
                              ...(handle.vertical === "top" ? { top: 4 } : { bottom: 4 }),
                              ...(handle.horizontal === "left" ? { left: 4 } : { right: 4 }),
                              width: 24,
                              height: 24,
                              zIndex: 24,
                              cursor: handle.cursor,
                              touchAction: "none",
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 7,
                              background: hovered ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.88)",
                              border: hovered ? "1px solid rgba(30,42,120,0.34)" : "1px solid rgba(30,42,120,0.12)",
                              color: "rgba(30,42,120,0.88)",
                              fontSize: 15,
                              lineHeight: 1,
                              userSelect: "none",
                              boxSizing: "border-box",
                            }}
                          ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>{handle.glyph}</span></div>
                        );
                      })}
                      {previewEdgeHandles.map((handle) => {
                        const hovered = previewResizeHover === handle.handle;
                        return (
                          <div
                            key={`image-preview-resize-edge-${handle.handle}`}
                            role="button"
                            tabIndex={-1}
                            {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
                            data-docwriter-image-preview-resize-edge={handle.edge}
                            aria-label={`Resize preview image height from ${handle.edge} edge`}
                            title={boolValue(normalizedDraftForPreview.lockAspectRatio, true) ? "Resize image proportionally" : "Resize image height"}
                            onPointerEnter={() => setPreviewResizeHover(handle.handle)}
                            onPointerLeave={() => setPreviewResizeHover("")}
                            onPointerDown={(event) => beginPreviewResize(event, handle.handle)}
                            style={{
                              position: "absolute",
                              left: 32,
                              right: 32,
                              ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }),
                              height: 14,
                              zIndex: 30,
                              cursor: handle.cursor,
                              touchAction: "none",
                              userSelect: "none",
                              background: "transparent",
                              pointerEvents: "auto",
                            }}
                          >
                            <span aria-hidden="true" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }), width: hovered ? 54 : 34, height: 2, borderRadius: 999, background: hovered ? "rgba(30,42,120,0.62)" : "rgba(30,42,120,0.20)", pointerEvents: "none", transition: "width 100ms ease, background 100ms ease" }} />
                          </div>
                        );
                      })}
                      <ImageSurface
                        src={draftSrc}
                        meta={normalizedDraftForPreview}
                        interactive
                        supplementalEditable
                        maxWidth={Math.max(maxWidth || 672, safeNumber(normalizedDraftForPreview.width, DEFAULT_WIDTH))}
                        onPositionDragStart={captureDraftCheckpoint}
                        onPositionChange={({ x, y }) => updateDraftMeta((current) => ({ ...current, positionX: Math.round(x * 10) / 10, positionY: Math.round(y * 10) / 10 }), { history: false })}
                        onSupplementalFocus={(kind, id) => setActiveSupplemental({ kind, id })}
                        onSupplementalInput={recordSupplementalTyping}
                        onSupplementalCommit={(kind, id, nextText) => updateSupplementalLine(kind, id, { text: nextText })}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 9, background: "#f8fafc", color: TEXT_MUTED, fontSize: 11, lineHeight: 1.4, border: "1px solid #e2e8f0" }}>
                  <div>Preview frame: {Math.round(safeNumber(normalizedDraftForPreview.width, DEFAULT_WIDTH))} × {Math.round(safeNumber(normalizedDraftForPreview.height, DEFAULT_HEIGHT))} px · {normalizeAlignment(normalizedDraftForPreview.alignment)} aligned · {normalizeFit(normalizedDraftForPreview.fit)}</div>
                  <div style={{ marginTop: 4 }}><strong>Alt text:</strong> {String(normalizedDraftForPreview.altText || "").trim() || "No alt text set"} <span style={{ fontWeight: 500 }}>(accessibility metadata; it is not printed as visible document text)</span></div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ flex: "0 0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "13px 18px 15px", borderTop: "1px solid rgba(148,163,184,0.22)", background: "#ffffff" }}>
            <button type="button" style={{ ...buttonStyle, color: "#b91c1c", borderColor: "#fecaca", background: "#fff7f7" }} onClick={() => setDeleteConfirmOpen(true)}>Delete image</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" style={buttonStyle} onClick={closeEditor}>Cancel</button>
              <button type="button" disabled={saving} style={{ ...buttonStyle, borderColor: ACCENT, background: ACCENT, color: "#ffffff", opacity: saving ? 0.7 : 1 }} onClick={saveEditor}>{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>

        {deleteConfirmOpen ? (
          <div
            {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
            role="presentation"
            onPointerDown={(event) => {
              event.stopPropagation();
              if (event.target === event.currentTarget) setDeleteConfirmOpen(false);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            style={{ position: "fixed", inset: 0, zIndex: 2147483100, display: "grid", placeItems: "center", padding: 20, boxSizing: "border-box", background: "rgba(15,23,42,0.38)" }}
          >
            <div role="alertdialog" aria-modal="true" aria-label="Confirm delete image" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()} style={{ width: "min(420px, calc(100vw - 40px))", padding: 20, borderRadius: 14, border: "1px solid rgba(148,163,184,0.35)", background: "#ffffff", boxShadow: "0 24px 70px rgba(15,23,42,0.30)", color: TEXT_DARK, boxSizing: "border-box" }}>
              <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.25 }}>Delete image?</div>
              <div style={{ marginTop: 7, fontSize: 12, lineHeight: 1.5, color: TEXT_MUTED }}>This will remove the image from the document. This action can be undone from the Canvas after the delete is committed.</div>
              <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="button" style={buttonStyle} onClick={() => setDeleteConfirmOpen(false)}>Cancel</button>
                <button type="button" style={{ ...buttonStyle, background: "#b91c1c", borderColor: "#b91c1c", color: "#ffffff" }} onClick={deleteImage}>Delete image</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>,
      document.body
    )
    : null;

  return (
    <div
      ref={rootRef}
      className={`doc-writer-image-shell ${className}`.trim()}
      {...{ [DOC_WRITER_IMAGE_ATTR]: "true" }}
      {...{ [DOC_WRITER_IMAGE_ID_ATTR]: actualImageId }}
      data-doc-writer-image-version={DOC_WRITER_IMAGE_COMPONENT_VERSION}
      data-doc-writer-image-selected={selected ? "true" : "false"}
      style={{
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
        margin: "14px 0 18px",
        paddingTop: canEdit ? 46 : 0,
        breakInside: "avoid",
        pageBreakInside: "avoid",
        fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        pointerEvents: "none",
      }}
    >
      <style {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}>{`
        @media print {
          .doc-writer-image-shell [${DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR}="true"],
          .doc-writer-image-edit-overlay { display: none !important; }
        }
      `}</style>

      {canEdit ? (
        <div
          {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
          style={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: "45%",
            maxWidth: 380,
            minWidth: 0,
            zIndex: 20,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "none",
          }}
        >
          <button
            type="button"
            {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true", [DOC_WRITER_IMAGE_DRAG_HANDLE_ATTR]: "true" }}
            aria-label="Move image"
            title="Move image — drag this handle"
            style={{
              width: 38,
              minWidth: 38,
              height: 34,
              minHeight: 34,
              padding: 0,
              borderRadius: 9,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              display: "grid",
              placeItems: "center",
              cursor: "grab",
              touchAction: "none",
              boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
              fontSize: 17,
              userSelect: "none",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
            onPointerDown={(event) => {
              if (event.button !== 0 || moveRef.current) return;
              event.preventDefault();
              event.stopPropagation();
              setResizeHover("");
              moveRef.current = { pointerId: event.pointerId };
              try { onEditingChange?.(true, { blockId: actualImageId, imageId: actualImageId, kind: "image", operation: "move" }); } catch (_) {}
              const finishMove = () => {
                window.removeEventListener("pointerup", finishMove, true);
                window.removeEventListener("pointercancel", finishMove, true);
                if (!moveRef.current) return;
                moveRef.current = null;
                try { onEditingChange?.(false, { blockId: actualImageId, imageId: actualImageId, kind: "image", operation: "move" }); } catch (_) {}
              };
              window.addEventListener("pointerup", finishMove, true);
              window.addEventListener("pointercancel", finishMove, true);
              try { onRequestDrag?.(event, { kind: "image", blockId: actualImageId, imageId: actualImageId, value: serializeCurrent(), operation: "move" }); } catch (_) {}
            }}
          ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>⠿</span></button>

          <button
            type="button"
            {...{ [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
            aria-label="Edit image"
            title="Edit image"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!editOpen) openEditor();
            }}
            onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}
            style={{
              minHeight: 34,
              height: 34,
              padding: "0 14px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: ACCENT,
              fontSize: 12,
              fontWeight: 850,
              cursor: "pointer",
              boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
              pointerEvents: "auto",
              boxSizing: "border-box",
            }}
          >Edit</button>
        </div>
      ) : null}

      <div
        className="doc-writer-image-presentation"
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: shellAlignment === "left" ? "flex-start" : shellAlignment === "right" ? "flex-end" : "center",
          alignItems: "flex-start",
          minWidth: 0,
          pointerEvents: "none",
        }}
      >
        <div
          className="doc-writer-image-canvas-presentation"
          style={{ position: "relative", display: "inline-block", maxWidth: "100%", boxSizing: "border-box", pointerEvents: "auto" }}
        >
          {canEdit ? [
            { handle: "nw", glyph: "↖", cursor: "nwse-resize", vertical: "top", horizontal: "left" },
            { handle: "ne", glyph: "↗", cursor: "nesw-resize", vertical: "top", horizontal: "right" },
            { handle: "sw", glyph: "↙", cursor: "nesw-resize", vertical: "bottom", horizontal: "left" },
            { handle: "se", glyph: "↘", cursor: "nwse-resize", vertical: "bottom", horizontal: "right" },
          ].map((handle) => {
            const hovered = resizeHover === handle.handle;
            return (
              <div
                key={`image-resize-${handle.handle}`}
                {...{ [DOC_WRITER_IMAGE_RESIZE_HANDLE_ATTR]: "true", [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
                role="button"
                tabIndex={-1}
                data-docwriter-image-resize-corner={handle.handle}
                aria-label={`Resize image from ${handle.vertical} ${handle.horizontal} corner`}
                title={boolValue(meta.lockAspectRatio, true) ? "Resize image proportionally" : "Resize image"}
                onPointerEnter={() => setResizeHover(handle.handle)}
                onPointerLeave={() => setResizeHover("")}
                onPointerDown={(event) => beginResize(event, handle.handle)}
                style={{
                  position: "absolute",
                  ...(handle.vertical === "top" ? { top: 4 } : { bottom: 4 }),
                  ...(handle.horizontal === "left" ? { left: 4 } : { right: 4 }),
                  width: 24,
                  height: 24,
                  zIndex: 24,
                  cursor: handle.cursor,
                  touchAction: "none",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 7,
                  background: hovered ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.74)",
                  border: hovered ? "1px solid rgba(30,42,120,0.34)" : "1px solid rgba(30,42,120,0.10)",
                  color: "rgba(30,42,120,0.88)",
                  fontSize: 15,
                  lineHeight: 1,
                  userSelect: "none",
                  boxSizing: "border-box",
                }}
              ><span aria-hidden="true" style={{ pointerEvents: "none", lineHeight: 1 }}>{handle.glyph}</span></div>
            );
          }) : null}

          {canEdit ? [
            { handle: "n", edge: "top", cursor: "ns-resize" },
            { handle: "s", edge: "bottom", cursor: "ns-resize" },
          ].map((handle) => {
            const hovered = resizeHover === handle.handle;
            return (
              <div
                key={`image-resize-edge-${handle.handle}`}
                {...{ [DOC_WRITER_IMAGE_RESIZE_HANDLE_ATTR]: "true", [DOC_WRITER_IMAGE_EDITOR_ONLY_ATTR]: "true" }}
                role="button"
                tabIndex={-1}
                data-docwriter-image-resize-edge={handle.edge}
                aria-label={`Resize image height from ${handle.edge} edge`}
                title={boolValue(meta.lockAspectRatio, true) ? "Resize image proportionally" : "Resize image height"}
                onPointerEnter={() => setResizeHover(handle.handle)}
                onPointerLeave={() => setResizeHover("")}
                onPointerDown={(event) => beginResize(event, handle.handle)}
                style={{
                  position: "absolute",
                  left: 32,
                  right: 32,
                  ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }),
                  height: 14,
                  zIndex: 30,
                  cursor: handle.cursor,
                  touchAction: "none",
                  userSelect: "none",
                  boxSizing: "border-box",
                  background: "transparent",
                  pointerEvents: "auto",
                }}
              >
                <span aria-hidden="true" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", ...(handle.edge === "top" ? { top: 0 } : { bottom: 0 }), width: hovered ? 54 : 34, height: 2, borderRadius: 999, background: hovered ? "rgba(30,42,120,0.62)" : "rgba(30,42,120,0.16)", pointerEvents: "none", transition: "width 100ms ease, background 100ms ease" }} />
              </div>
            );
          }) : null}

          <div
            ref={printSurfaceRef}
            className="doc-writer-image-print-surface"
            {...{ [DOC_WRITER_IMAGE_PRINT_ATTR]: "true" }}
            data-doc-writer-image-alignment={shellAlignment}
            style={{ position: "relative", display: "inline-block", maxWidth: "100%", boxSizing: "border-box" }}
          >
            <ImageSurface
              src={src}
              meta={meta}
              maxWidth={maxWidth || MAX_WIDTH}
              supplementalEditable={canEdit && !editOpen}
              onSupplementalCommit={commitLiveSupplementalText}
              canvasTextAlignmentHandoff
            />
          </div>
        </div>
      </div>

      {editDialog}
    </div>
  );
});

export default DocWriterImage;
